import React, { FC, Suspense, useContext, useEffect } from 'react';
import {
  Box,
  Text,
  Icon,
  IconSize,
  useSideSheetState,
  useMediaQuery,
  belowBreakpoint,
  Theme,
  useToastsDispatch,
} from '@bechtel/gds';
import { TextStyled } from './styled';
import { WorkPackage, Scalars, CrewMemberAssignment } from '@bechtel/types';
import { useWorkPackagePermissions } from '~/hooks/use-work-package-permissions';
import { usePlanning, useDateRange, SuspenseLoader, TOAST_ID_WORK_PACKAGE } from '@bechtel/client-common';
import { ThemeContext } from 'styled-components';
import PlanningDateWindow, { workPackagesOutsideWLA } from './planning-date-window';
import { EditWorkPackageSchedule } from '~/components/organisms/edit-work-package-schedule';
import { t } from '@lingui/macro';

export interface WorkPackageHeaderProps {
  workPackageTag: WorkPackage['tag'];
  startDate?: WorkPackage['startDate'];
  endDate?: WorkPackage['endDate'];
  selectedWorkpackageList?: WorkPackage[];
  showRemainingTime?: Scalars['Boolean'];
  workOwners?: CrewMemberAssignment[];
  isPlanningModule?: Scalars['Boolean'];
}

export const WorkPackageHeader: FC<WorkPackageHeaderProps> = ({
  workPackageTag,
  startDate,
  endDate,
  selectedWorkpackageList,
  showRemainingTime = false,
  isPlanningModule = false,
  workOwners,
}) => {
  const { weekStartDate, weekEndDate } = usePlanning();
  const [dateRangeLabel, dateDistance] = useDateRange(startDate, endDate);
  const { visible, open, close } = useSideSheetState(false);
  const { canEditSchedule } = useWorkPackagePermissions(workOwners);
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const isDesktop = useMediaQuery(belowBreakpoint(breakpoints.desktop));
  const dispatchToast = useToastsDispatch();
  let workPackageListOutsideWLA: WorkPackage[] = [];
  if (isPlanningModule && selectedWorkpackageList && selectedWorkpackageList.length)
    workPackageListOutsideWLA = workPackagesOutsideWLA(selectedWorkpackageList, weekStartDate, weekEndDate);
  useEffect(() => {
    if (workPackageListOutsideWLA.length == 0)
      dispatchToast({ type: 'REMOVE_TOAST', payload: { id: TOAST_ID_WORK_PACKAGE } });
  }, [dispatchToast, workPackageListOutsideWLA]);
  return (
    <Box
      pl={{ sm: '600' }}
      pt={{ sm: '500' }}
      pb={{ sm: '400' }}
      flexDirection={{ sm: 'column' }}
      justifyContent={{ sm: 'center' }}
      css={
        selectedWorkpackageList && selectedWorkpackageList.length > 0 && canEditSchedule
          ? 'cursor: pointer;'
          : 'cursor: auto;'
      }
      onClick={open}
    >
      <Box py={{ sm: '200' }}>
        <Text
          size={
            isPlanningModule && isDesktop && selectedWorkpackageList && selectedWorkpackageList?.length > 1
              ? '500'
              : '600'
          }
          color="white"
          weight="600"
          transform="uppercase"
        >
          {selectedWorkpackageList && selectedWorkpackageList.length > 1
            ? `${t`Multiple Work Packages`} ${isDesktop ? '' : t`Selected`}(${selectedWorkpackageList?.length})`
            : workPackageTag
            ? workPackageTag
            : t`No Work Package Selected`}
        </Text>
      </Box>
      {!showRemainingTime ? (
        <Box mt={{ sm: '200' }} pt={{ sm: '200' }} pb={{ sm: '300' }}>
          {selectedWorkpackageList && selectedWorkpackageList.length > 0 && (
            <>
              <Icon size={IconSize.MEDIUM} icon="calendar-day" fillColor="white"></Icon>
              <TextStyled size={isPlanningModule && isDesktop ? '300' : '400'}>{dateRangeLabel}</TextStyled>
              {isPlanningModule && workPackageListOutsideWLA.length > 0 && (
                <PlanningDateWindow
                  workPackageOutsideWLACount={workPackageListOutsideWLA.length}
                  selectedWorkPackageCount={selectedWorkpackageList.length}
                />
              )}
            </>
          )}
        </Box>
      ) : (
        <Box mb={{ sm: '400' }} pt={{ sm: '100' }} pb={{ sm: '100' }} alignItems={{ sm: 'center' }}>
          {selectedWorkpackageList && selectedWorkpackageList.length > 0 && (
            <>
              <Box pr={{ sm: '400' }}>
                <Icon size={IconSize.MEDIUM} icon="calendar-day" fillColor="white"></Icon>
              </Box>
              <Box pt={{ sm: '200' }}>
                <Text size="400" color="white">
                  {dateRangeLabel} {dateDistance}
                </Text>
              </Box>
              {isPlanningModule && workPackageListOutsideWLA.length > 0 && (
                <PlanningDateWindow
                  workPackageOutsideWLACount={workPackageListOutsideWLA.length}
                  selectedWorkPackageCount={selectedWorkpackageList.length}
                />
              )}
            </>
          )}
        </Box>
      )}
      <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
        {canEditSchedule && visible && selectedWorkpackageList && selectedWorkpackageList.length > 0 && (
          <EditWorkPackageSchedule
            workPackageList={selectedWorkpackageList}
            startDate={startDate}
            endDate={endDate}
            onClose={close}
            workPackageTag={workPackageTag}
            isPlanningModule={isPlanningModule}
          ></EditWorkPackageSchedule>
        )}
      </Suspense>
    </Box>
  );
};
